import React, { useState, useCallback, useEffect } from "react";
import styled from "styled-components";

import AppIcon from "../../../components/Core/AppIcon/AppIcon";
import Flex from "../../../components/Layout/Flex";
import { appTheme } from "../../../theme/Theme";
import { NotificationSettings } from "../../../models/UserCustomers";
import { Popover } from "frost-ui-react";
import { useTranslation } from "react-i18next";
import { PopoverProps } from "frost-ui-react/dist/Popover/Popover";

interface NotificationButtonsProps {
	editable: boolean;
	notifications?: NotificationSettings;
	onNotificationUpdate?: Function;
}

interface NotificationButtonsStylesProps {
	active?: boolean;
}

const NotificationButtons: React.FC<NotificationButtonsProps> = ({
	notifications,
	editable,
	onNotificationUpdate,
}) => {
	const [jobConfirmation, setJobConfirmation] = useState(!!notifications?.jobConfirmation);
	const [jobReport, setJobReport] = useState(!!notifications?.jobReport);
	const [timeReportSummery, setTimeReportSummery] = useState(!!notifications?.timeReportSummery);
	const { t } = useTranslation();

	const notificationSetting: NotificationSettings = {
		jobConfirmation,
		jobReport,
		timeReportSummery,
	};

	const handleJobConfirmation = useCallback(
		(e: any) => {
			e.preventDefault();
			e.stopPropagation();
			setJobConfirmation(!jobConfirmation);
			onNotificationUpdate &&
				onNotificationUpdate({
					...notificationSetting,
					jobConfirmation: !jobConfirmation,
				});
		},
		[setJobConfirmation, jobConfirmation, onNotificationUpdate, notificationSetting]
	);

	const handleJobReport = useCallback(
		(e: any) => {
			e.preventDefault();
			e.stopPropagation();
			setJobReport(!jobReport);
			onNotificationUpdate &&
				onNotificationUpdate({ ...notificationSetting, jobReport: !jobReport });
		},
		[setJobReport, jobReport, onNotificationUpdate, notificationSetting]
	);

	const handleTimeReportSummary = useCallback(
		(e: any) => {
			e.preventDefault();
			e.stopPropagation();
			setTimeReportSummery(!timeReportSummery);
			onNotificationUpdate &&
				onNotificationUpdate({
					...notificationSetting,
					timeReportSummery: !timeReportSummery,
				});
		},
		[setTimeReportSummery, timeReportSummery, onNotificationUpdate, notificationSetting]
	);

	useEffect(() => {
		setJobConfirmation(!!notifications?.jobConfirmation);
		setJobReport(!!notifications?.jobReport);
		setTimeReportSummery(!!notifications?.timeReportSummery);
	}, [notifications, setJobConfirmation, setJobReport, setTimeReportSummery]);

	const popoverProps: Omit<PopoverProps, "children"> = {
		pointTo: "top",
		styles: { width: 150, offset: "60px" },
	};

	return (
		<div>
			<Flex>
				<IconWrapper onClick={handleJobConfirmation} active={jobConfirmation} disabled={!editable}>
					<AppIcon icon={jobConfirmation ? "confirmation-green" : "confirmation-ash"} size="14px" />
					<div className="popover-wrapper">
						<Popover {...popoverProps}>
							{t("customers.multipleCustomers.notificationTypes.booking")}
						</Popover>
					</div>
				</IconWrapper>
				<IconWrapper
					onClick={handleTimeReportSummary}
					active={timeReportSummery}
					disabled={!editable}
				>
					<AppIcon icon={timeReportSummery ? "clock-green" : "clock-ash"} size="14px" />
					<div className="popover-wrapper">
						<Popover {...popoverProps}>
							{t("customers.multipleCustomers.notificationTypes.timeReport")}
						</Popover>
					</div>
				</IconWrapper>
				<IconWrapper onClick={handleJobReport} active={jobReport} disabled={!editable}>
					<AppIcon icon={jobReport ? "briefcase-green" : "briefcase-ash"} size="14px" />
					<div className="popover-wrapper">
						<Popover {...popoverProps}>
							{t("customers.multipleCustomers.notificationTypes.jobReport")}
						</Popover>
					</div>
				</IconWrapper>
			</Flex>
		</div>
	);
};
export default NotificationButtons;

const IconWrapper = styled.button<NotificationButtonsStylesProps>`
	width: 34px;
	height: 24px;
	border: ${(props) =>
		props.active ? `2px solid ${appTheme.colors.COLOR_ACNE_PRIMARY}` : "2px solid #D4D4D4"};
	border-radius: 3px;
	display: flex;
	justify-content: center;
	align-items: center;
	margin-right: 10px;
	background: ${appTheme.colors.COLOR_WHITE};
	position: relative;
	&:hover {
		cursor: pointer;
	}
	&:disabled {
		&:hover {
			cursor: default;
		}
	}

	.popover-wrapper {
		position: absolute;
		top: 34px;
		display: none;
		z-index: 3;
	}

	&:hover .popover-wrapper {
		display: block;
	}
`;
